import { ApolloClient } from 'apollo-client';

// Store paused polling intervals per query id
let savedPollingIntervals: Map<string, number> | null = null;
let paused = false;

function forEachObservableQuery(client: ApolloClient<any>, cb: (oq: any, id: string) => void) {
  const qm: any = (client as any).queryManager;
  if (!qm) return;
  const store: any = qm.observableQueries || qm.queries;
  if (!store) return;
  if (store.forEach && typeof store.forEach === 'function') {
    // Map-like
    store.forEach((oq: any, key: any) => {
      // Some versions store entries as { observableQuery: oq }
      const entry = (oq && oq.observableQuery) ? oq.observableQuery : oq;
      const id = String((entry && entry.queryId) || key);
      if (entry) cb(entry, id);
    });
  } else {
    // Object-like fallback
    Object.keys(store).forEach((key) => {
      const entry = store[key]?.observableQuery || store[key];
      if (entry) cb(entry, key);
    });
  }
}

export function pauseAllPolling(client: ApolloClient<any>) {
  if (paused) {
    console.debug('[Polling] Already paused; ignoring pauseAllPolling');
    return;
  }
  const affected: string[] = [];
  savedPollingIntervals = new Map();
  forEachObservableQuery(client, (oq: any, id: string) => {
    const interval: number = (oq as any).pollInterval || (oq.options && oq.options.pollInterval) || 0;
    if (interval && typeof oq.stopPolling === 'function') {
      try {
        savedPollingIntervals!.set(id, interval);
        affected.push(id);
        oq.stopPolling();
      } catch (e) {
        console.debug('[Polling] stopPolling failed for', id, e);
      }
    }
  });
  paused = true;
  console.debug(`[Polling] Paused ${affected.length} queries`, affected.length ? { ids: affected } : '');
}

export function resumeAllPolling(client: ApolloClient<any>, refetch: boolean = false) {
  if (!paused) {
    console.debug('[Polling] Not paused; ignoring resumeAllPolling');
    return;
  }
  if (!savedPollingIntervals) {
    paused = false;
    console.debug('[Polling] No saved intervals to resume');
    return;
  }
  const restarted: string[] = [];
  forEachObservableQuery(client, (oq: any, id: string) => {
    const interval = savedPollingIntervals!.get(id);
    if (interval && typeof oq.startPolling === 'function') {
      try {
        oq.startPolling(interval);
        if (refetch){
            oq.refetch();
        }
        restarted.push(id);
      } catch (e) {
        console.debug('[Polling] startPolling failed for', id, e);
      }
    }
  });
  savedPollingIntervals = null;
  paused = false;
  console.debug(`[Polling] Resumed ${restarted.length} queries`, restarted.length ? { ids: restarted } : '');
}

export function isPollingPaused() {
  return paused;
}
